import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/AuthContext";
import { useLanguage } from "@/lib/LanguageContext";
import { toast } from "sonner";
import { getAdminInvoices, openInvoicePdf, generateBulkInvoiceSummary, generateBulkInvoiceSummaryPdf } from "@/lib/api";
import { ArrowLeft, Calendar, FileDown, Printer, Share2, Loader2, X, Filter, FileSpreadsheet, CheckSquare, Square, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

export default function BulkInvoicesPage() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const { t, language } = useLanguage();
  const isOwner = admin?.role === "owner" || admin?.role === "admin";

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [displayInvoices, setDisplayInvoices] = useState([]);
  const [totalSelected, setTotalSelected] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(function checkRole() { if (admin && !isOwner) navigate("/admin"); }, [admin, isOwner, navigate]);

  const fetchInvoices = async function() {
    setLoading(true);
    try {
      const data = await getAdminInvoices("paid");
      const arr = Array.isArray(data) ? data : [];
      setInvoices(arr);
      setDisplayInvoices(arr);
    } catch (err) { toast.error(t("toasts.invoiceLoadError")); }
    finally { setLoading(false); }
  };

  useEffect(function loadData() { fetchInvoices(); }, []);

  useEffect(function filterByDate() {
    if (!dateFrom && !dateTo) { setDisplayInvoices(invoices); return; }
    const fromTs = dateFrom ? new Date(dateFrom).setHours(0, 0, 0, 0) : null;
    const toTs = dateTo ? new Date(dateTo).setHours(23, 59, 59, 999) : null;
    const filtered = [];
    for (var i = 0; i < invoices.length; i++) {
      var inv = invoices[i];
      var ts = inv.paid_at ? new Date(inv.paid_at).getTime() : null;
      if (!ts) { filtered.push(inv); continue; }
      if (fromTs && ts < fromTs) continue;
      if (toTs && ts > toTs) continue;
      filtered.push(inv);
    }
    setDisplayInvoices(filtered);
  }, [invoices, dateFrom, dateTo]);

  useEffect(function calcTotal() {
    var sum = 0;
    for (var i = 0; i < displayInvoices.length; i++) {
      if (selectedIds.has(displayInvoices[i].id)) {
        sum += displayInvoices[i].amount_total || 0;
      }
    }
    setTotalSelected(sum);
  }, [displayInvoices, selectedIds]);

  function toggleSelect(id) { 
    var s = new Set(selectedIds); 
    if (s.has(id)) s.delete(id); else s.add(id); 
    setSelectedIds(s); 
  }
  
  function toggleSelectAll() { 
    if (selectedIds.size === displayInvoices.length) { 
      setSelectedIds(new Set()); 
    } else { 
      var s = new Set();
      for (var i = 0; i < displayInvoices.length; i++) s.add(displayInvoices[i].id);
      setSelectedIds(s); 
    } 
  }
  
  function clearFilter() { setDateFrom(""); setDateTo(""); setSelectedIds(new Set()); }
  
  function formatDate(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  async function handleBulkPrint() { 
    if (selectedIds.size === 0) return; 
    var ids = Array.from(selectedIds);
    setBulkLoading(true);
    try {
      for (var i = 0; i < ids.length; i++) {
        await openInvoicePdf(ids[i]);
      }
      toast.success(ids.length + " PDF-uri deschise"); 
    } catch (err) {
      toast.error(t("toasts.invoicePrintError"));
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleGenerateSummary() {
    if (selectedIds.size === 0) return;
    setGenLoading(true);
    try { 
      var data = await generateBulkInvoiceSummary(Array.from(selectedIds)); 
      setSummaryData(data); 
      setShowModal(true); 
      toast.success(t("bulkInvoices.summaryGenerated")); 
    }
    catch (err) { toast.error(t("toasts.dataLoadError")); }
    finally { setGenLoading(false); }
  }

  async function handleDownloadPdf() {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      var blob = await generateBulkInvoiceSummaryPdf(Array.from(selectedIds));
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url; a.download = "RTS_Summary.pdf"; document.body.appendChild(a); a.click(); 
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success(t("bulkInvoices.pdfsReady"));
    } catch (err) { toast.error(t("toasts.invoicePrintError")); }
    finally { setBulkLoading(false); }
  }

  async function handlePrint() {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try { 
      var blob = await generateBulkInvoiceSummaryPdf(Array.from(selectedIds)); 
      var url = window.URL.createObjectURL(blob); 
      window.open(url, "_blank"); 
      toast.success(t("bulkInvoices.pdfsReady")); 
    }
    catch (err) { toast.error(t("toasts.invoicePrintError")); }
    finally { setBulkLoading(false); }
  }

  async function handleShare() {
    if (!summaryData || selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      // Generate the Summary PDF
      var blob = await generateBulkInvoiceSummaryPdf(Array.from(selectedIds));
      var pdfFile = new File([blob], "RTS_Invoice_Summary.pdf", { type: "application/pdf" });
      
      // Check if Web Share API with files is supported
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        // Use native share with PDF attachment
        await navigator.share({
          title: "RTS Express - Invoice Summary Report",
          text: "Invoice Summary Report - " + summaryData.count + " invoices | Total: £" + summaryData.total_amount.toFixed(2),
          files: [pdfFile]
        });
        toast.success(t("bulkInvoices.shareSuccess") || "Document shared successfully");
      } else if (navigator.share) {
        // Fallback: Web Share API without file support - share text with download link
        await navigator.share({
          title: "RTS Express - Invoice Summary Report",
          text: "RTS Express Invoice Summary\n" + summaryData.count + " invoices | Total: £" + summaryData.total_amount.toFixed(2) + "\n\nPlease contact us for the full PDF report."
        });
        // Also download the PDF for the user
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url; a.download = "RTS_Invoice_Summary.pdf"; document.body.appendChild(a); a.click();
        window.URL.revokeObjectURL(url); document.body.removeChild(a);
        toast.success(t("bulkInvoices.shareSuccess") || "Shared! PDF also downloaded.");
      } else {
        // Fallback for browsers without Web Share API: Download PDF and open email
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url; a.download = "RTS_Invoice_Summary.pdf"; document.body.appendChild(a); a.click();
        window.URL.revokeObjectURL(url); document.body.removeChild(a);
        
        // Open email client with summary info
        var subject = encodeURIComponent("RTS Express - Invoice Summary Report");
        var body = encodeURIComponent("Invoice Summary Report\n\nTotal: £" + summaryData.total_amount.toFixed(2) + "\nInvoices: " + summaryData.count + "\n\nPlease find the attached PDF document (downloaded separately).");
        window.open("mailto:?subject=" + subject + "&body=" + body, "_blank");
        toast.success(t("bulkInvoices.pdfDownloaded") || "PDF downloaded. Email client opened.");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Share error:", err);
        toast.error(t("toasts.shareError") || "Share failed");
      }
    } finally {
      setBulkLoading(false);
    }
  }

  function renderInvoiceRows() {
    var rows = [];
    for (var i = 0; i < displayInvoices.length; i++) {
      var inv = displayInvoices[i];
      rows.push(
        <TableRow key={inv.id} className={cn("border-slate-800 hover:bg-slate-800/30", selectedIds.has(inv.id) && "bg-slate-800/50")}>
          <TableCell><Checkbox checked={selectedIds.has(inv.id)} onCheckedChange={function(id){ return function(){ toggleSelect(id); }; }(inv.id)} className="border-slate-600" /></TableCell>
          <TableCell><div><p className="text-white font-medium">{inv.invoice_number}</p><p className="text-slate-500 text-xs">#{inv.quote_id?.slice(0, 8)}</p></div></TableCell>
          <TableCell><div><p className="text-white font-medium">{inv.company_name || "—"}</p><p className="text-slate-500 text-xs">{inv.contact_name || "—"}</p></div></TableCell>
          <TableCell><span className="text-slate-300 text-sm">{formatDate(inv.paid_at)}</span></TableCell>
          <TableCell><span className="text-[#22c55e] font-bold">£{(inv.amount_total || 0).toFixed(2)}</span></TableCell>
          <TableCell className="text-right"><Button size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" onClick={function(id){ return async function(){ try { await openInvoicePdf(id); } catch(e) { toast.error(t("toasts.invoicePrintError")); } }; }(inv.id)}><FileDown className="w-4 h-4 mr-1" />PDF</Button></TableCell>
        </TableRow>
      );
    }
    return rows;
  }

  function renderSummaryRows() {
    if (!summaryData || !summaryData.items) return null;
    var rows = [];
    for (var i = 0; i < summaryData.items.length; i++) {
      var item = summaryData.items[i];
      rows.push(
        <tr key={i} className="border-t border-slate-700">
          <td className="px-3 py-2 text-slate-500">{i + 1}</td>
          <td className="px-3 py-2 text-white font-medium">{item.invoice_number}</td>
          <td className="px-3 py-2 text-slate-300">{item.company_name}</td>
          <td className="px-3 py-2 text-slate-300">{item.date}</td>
          <td className="px-3 py-2 text-slate-300">{item.time}</td>
          <td className="px-3 py-2 text-right text-[#22c55e] font-medium">£{item.amount.toFixed(2)}</td>
        </tr>
      );
    }
    return rows;
  }

  if (!isOwner) return null;

  return (
    <div className="min-h-screen bg-[#020617]">
      <header className="bg-[#0f172a] border-b border-slate-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <div><h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-white">{t("bulkInvoices.bulkActions")}</h1></div>
          </div>
          <Button onClick={fetchInvoices} variant="outline" className="border-slate-700 text-slate-400"><RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />{t("headers.refresh")}</Button>
        </div>
      </header>

      <main className="p-6">
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-4 mb-4">
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="text-slate-400 text-sm">{t("bulkInvoices.filterByPeriod")}</span>
            <Input type="date" value={dateFrom} onChange={function(e){ setDateFrom(e.target.value); }} className="w-40 bg-slate-900 border-slate-700 text-white text-sm" />
            <Input type="date" value={dateTo} onChange={function(e){ setDateTo(e.target.value); }} className="w-40 bg-slate-900 border-slate-700 text-white text-sm" />
            {(dateFrom || dateTo) && <Button variant="ghost" size="sm" onClick={clearFilter} className="text-slate-400"><X className="w-4 h-4" /></Button>}
          </div>
          {(dateFrom || dateTo) && <div className="bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2 mb-4"><Filter className="w-4 h-4 text-amber-400 inline mr-2" /><span className="text-amber-400 text-sm">{dateFrom} → {dateTo} ({displayInvoices.length})</span></div>}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={toggleSelectAll} className="border-slate-700 text-slate-300">
                {selectedIds.size === displayInvoices.length && displayInvoices.length > 0 ? <CheckSquare className="w-4 h-4 mr-2 text-[#22c55e]" /> : <Square className="w-4 h-4 mr-2" />}
                {selectedIds.size === displayInvoices.length ? t("bulkInvoices.deselectAll") : t("bulkInvoices.selectAll")} ({displayInvoices.length})
              </Button>
              {selectedIds.size > 0 && <span className="text-[#22c55e] font-bold">{selectedIds.size} sel. | £{totalSelected.toFixed(2)}</span>}
            </div>
            {selectedIds.size > 0 && <div className="flex gap-2">
              <Button size="sm" onClick={handleBulkPrint} disabled={bulkLoading} className="bg-slate-700">{bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}</Button>
              <Button size="sm" onClick={handleDownloadPdf} disabled={bulkLoading} className="bg-blue-600">{bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}</Button>
              <Button size="sm" onClick={handleGenerateSummary} disabled={genLoading} className="bg-[#22c55e] text-black">{genLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}</Button>
            </div>}
          </div>
        </div>

        <div className="bg-[#0f172a] border border-slate-800 rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow className="border-slate-800"><TableHead className="w-12"><Checkbox checked={selectedIds.size === displayInvoices.length && displayInvoices.length > 0} onCheckedChange={toggleSelectAll} /></TableHead><TableHead className="text-slate-400">{t("invoicesTable.invoice")}</TableHead><TableHead className="text-slate-400">{t("invoicesTable.client")}</TableHead><TableHead className="text-slate-400">{t("invoicesTable.paidAt")}</TableHead><TableHead className="text-slate-400">{t("invoicesTable.amount")}</TableHead><TableHead className="text-slate-400 text-right">{t("tableHeaders.actions")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>}
              {!loading && displayInvoices.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">{t("invoicesTable.noInvoices")}</TableCell></TableRow>}
              {!loading && displayInvoices.length > 0 && renderInvoiceRows()}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#1e293b] border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#22c55e]" />{t("bulkInvoices.summaryReport")}</DialogTitle><DialogDescription className="text-slate-400">{summaryData?.count} invoices | £{summaryData?.total_amount?.toFixed(2)}</DialogDescription></DialogHeader>
          {summaryData && <div className="space-y-4">
            <div className="bg-slate-900 rounded border border-slate-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800"><th className="text-left px-3 py-2 text-slate-400">#</th><th className="text-left px-3 py-2 text-slate-400">{t("bulkInvoices.invoiceNumber")}</th><th className="text-left px-3 py-2 text-slate-400">{t("bulkInvoices.client")}</th><th className="text-left px-3 py-2 text-slate-400">{t("bulkInvoices.date")}</th><th className="text-left px-3 py-2 text-slate-400">{t("bulkInvoices.time")}</th><th className="text-right px-3 py-2 text-slate-400">{t("bulkInvoices.amount")}</th></tr></thead>
                <tbody>{renderSummaryRows()}</tbody>
                <tfoot><tr className="bg-[#22c55e]/20 border-t-2 border-[#22c55e]"><td colSpan={5} className="px-3 py-3 text-right text-white font-bold">{t("bulkInvoices.grandTotal")}:</td><td className="px-3 py-3 text-right text-[#22c55e] font-bold text-lg">£{summaryData.total_amount.toFixed(2)}</td></tr></tfoot>
              </table>
            </div>
            <div className="flex gap-2 pt-4 border-t border-slate-700">
              <Button onClick={handlePrint} disabled={bulkLoading} className="bg-slate-700"><Printer className="w-4 h-4 mr-2" /></Button>
              <Button onClick={handleDownloadPdf} disabled={bulkLoading} className="bg-blue-600"><FileDown className="w-4 h-4 mr-2" /></Button>
              <Button onClick={handleShare} disabled={bulkLoading} className="bg-green-600"><Share2 className="w-4 h-4 mr-2" /></Button>
              <Button variant="outline" onClick={function(){ setShowModal(false); }} className="ml-auto border-slate-600 text-slate-300">{t("bulkInvoices.close")}</Button>
            </div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
